import classNames from 'classnames';
import React from 'react';

import {ReviewAction, ReviewHistoryItem} from 'notebook/reviews/ReviewConstants';
import {actionAndStatusText} from 'notebook/reviews/ReviewUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {formalTimeUTC} from 'util/DateUtil';
import {typeToName} from 'util/NameUtil';

export interface ReviewHistoryContainerProps {
  reviewHistoryItems: Array<ReviewHistoryItem>;
  showReviewCommentHistory: boolean;
  reviewableId: string;
}

interface ReviewHistoryItemProps {
  item: ReviewHistoryItem;
  beforeStatus: string | null;
  afterStatus: string | null;
  reviewStage: string;
  stageDefinition?: string;
  showReviewCommentHistory: boolean;
  reviewProcessName?: string;
  reviewProcessId?: string;
  actionText: string;
}

function ReviewHistoryItemComponent({
  item,
  beforeStatus,
  afterStatus,
  reviewStage,
  stageDefinition,
  showReviewCommentHistory,
  reviewProcessName,
  reviewProcessId,
  actionText,
}: ReviewHistoryItemProps): JSX.Element {
  const signatureData = item.info?.signatureData;
  const hasReviewComment = showReviewCommentHistory && Boolean(item.info.message);
  const reviewerInformation = {
    reviewerName: signatureData?.userFullName ?? item.user.name,
    reviewerHandle: signatureData?.userHandle ?? item.user.handle,
    reviewerEmail: signatureData?.userEmail ?? item.user.email,
    reviewerUserId: signatureData?.userId ?? item.user.id,
    timestamp: item.time,
    ipAddress: signatureData?.ipAddress ?? '-',
    authProvider: signatureData?.authenticationMethod, // If null/undefined, we won't show the signature-auth-provider-badge
    signatureVersion: signatureData?.version,
    reviewHistoryUiVersion: signatureData?.reviewHistoryUiVersion,
  };

  return (
    <>
      <tr
        className={classNames('review-history-item', {
          'review-history-item-with-comment': hasReviewComment,
        })}
      >
        <td className="review-item-user-column">
          <div className="review-item-signature-card">
            <div className="signature-user-name">
              <span className="review-item-label">Name:</span>
              {reviewerInformation.reviewerName}
            </div>
            {reviewerInformation.authProvider && (
              <>
                <div className="signature-auth-provider-badge">
                  <BenchlingIcon className="signature-auth-provider-icon" icon="esign" />
                  <div>
                    e-signed with SAML
                    <div className="signature-ip">
                      <span className="review-item-label">IP Address:</span>
                      {reviewerInformation.ipAddress || '-'}
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="signature-user-handle">
              <span className="review-item-label">Username:</span>
              {`${reviewerInformation.reviewerHandle || ''} (${reviewerInformation.reviewerUserId})`}
            </div>
            <div className="signature-user-email">
              <span className="review-item-label">Email:</span>
              {reviewerInformation.reviewerEmail || ''}
            </div>
            <div className="signature-timestamp">
              <span className="review-item-label">Timestamp:</span>
              {formalTimeUTC(reviewerInformation.timestamp)}
            </div>
          </div>
        </td>
        <td className="review-item-review-process-column">
          <div className="review-item-value">
            <div>{reviewProcessName || 'Auditor-Based Review'}</div>
            {reviewProcessId && <div>({reviewProcessId})</div>}
          </div>
        </td>
        <td className="review-item-stage-column">
          <div className="review-item-value">{reviewStage}</div>
        </td>
        <td className="review-item-action-column">
          <div className="review-item-value">
            <strong>{actionText}</strong>
            <br />
            {stageDefinition}
          </div>
        </td>
        <td className="review-item-status-before-column">
          <div className="review-item-value">{beforeStatus}</div>
        </td>
        <td className="review-item-status-after-column">
          <div className="review-item-value">{afterStatus}</div>
        </td>
        {/* </div> */}
      </tr>
      {hasReviewComment && (
        <tr className="review-item-comment">
          <td colSpan={6}>
            <span className="review-item-label">Comment:</span>
            <span className="review-item-value">{item.info.message}</span>
          </td>
        </tr>
      )}
    </>
  );
}

interface ReviewHistoryItemsListProps {
  reviewHistoryItems: Array<ReviewHistoryItem>;
  showReviewCommentHistory: boolean;
  reviewableNoun: string;
}

function ReviewHistoryItemsList({
  reviewHistoryItems,
  showReviewCommentHistory,
  reviewableNoun,
}: ReviewHistoryItemsListProps): JSX.Element | null {
  // Filter items if not showing comment history
  let filteredItems = reviewHistoryItems;
  if (!showReviewCommentHistory) {
    filteredItems = reviewHistoryItems.filter((item) => item.info.reviewAction !== ReviewAction.COMMENT);
  }

  if (filteredItems.length === 0) {
    return null;
  }

  return (
    <table className="review-history-table">
      <colgroup>
        <col style={{width: '220px'}} />
        <col style={{width: '110px'}} />
        <col style={{width: '60px'}} />
        <col style={{width: '100%'}} />
        <col style={{width: '60px'}} />
        <col style={{width: '60px'}} />
      </colgroup>
      <thead>
        <tr>
          <th className="review-item-user-column">
            <div className="review-item-label">User</div>
          </th>
          <th className="review-item-review-process-column">
            <div className="review-item-label">Review Process</div>
          </th>
          <th className="review-item-stage-column">
            <div className="review-item-label">Stage</div>
          </th>
          <th className="review-item-action-column">
            <div className="review-item-label">Action / Definition</div>
          </th>
          <th className="review-item-status-before-column">
            <div className="review-item-label">Status Before</div>
          </th>
          <th className="review-item-status-after-column">
            <div className="review-item-label">Status After</div>
          </th>
        </tr>
      </thead>
      <tbody>
        {filteredItems.map((item, index) => {
          const stageDefinition = item.reviewLifecycleInfo?.reviewStageInfo?.definition;
          const reviewProcessName = item.reviewLifecycleInfo?.name;
          const reviewProcessId = item.reviewLifecycleInfo?.apiIdentifier;

          // Get review stage information
          const reviewStage = item.reviewLifecycleInfo?.reviewStageInfo?.name || '';

          // Get action and status for "Status After" using the same method as EntryExportMetadata.tsx
          const {action, newEntryStatus: newStatusText} = actionAndStatusText(
            filteredItems,
            index,
            true,
            false,
            reviewableNoun
          );

          // Get status for "Status Before" column
          let beforeStatusText: string | null = null;
          if (index > 0) {
            const result = actionAndStatusText(filteredItems, index - 1, true, false, reviewableNoun);
            beforeStatusText = result.newEntryStatus;
          }

          return (
            <ReviewHistoryItemComponent
              key={index}
              item={item}
              beforeStatus={beforeStatusText}
              afterStatus={newStatusText}
              stageDefinition={stageDefinition}
              reviewStage={reviewStage}
              showReviewCommentHistory={showReviewCommentHistory}
              reviewProcessName={reviewProcessName}
              reviewProcessId={reviewProcessId}
              actionText={action}
            />
          );
        })}
      </tbody>
    </table>
  );
}

function ReviewHistoryContainer({
  reviewHistoryItems,
  showReviewCommentHistory,
  reviewableId,
}: ReviewHistoryContainerProps): JSX.Element {
  const reviewableNoun = typeToName(APIUtil.apiIdToType(reviewableId));
  return (
    <div className="review-history-container">
      <div className="review-history-content">
        <h3 className="review-history-section-title">Review History</h3>
        <ReviewHistoryItemsList
          reviewHistoryItems={reviewHistoryItems}
          showReviewCommentHistory={showReviewCommentHistory}
          reviewableNoun={reviewableNoun}
        />
      </div>
    </div>
  );
}

export default ReviewHistoryContainer;
